import { Space, Tag } from "../../../../../../shared/frontend/ui";
import React from "react";
import type {
  SharedBillingAddress,
  SharedBillingAddressBookAdapter,
  SharedBillingAddressEditorShellProps,
  SharedBillingAddressListContext,
} from "../../../../../../shared/frontend/billing/sharedBillingAddressTypes";
import { FormDrawer } from "../../components/FormDrawer";
import { SettingsListPage, type ColumnDefinition,type ProColumnsWithMeasure } from "../../components/SettingsListPage";
import { listActionPreset, makeActionColumn } from "../../components/ui/antd";
import type { ApiClient } from "../api/client";

type Translate = (key: string) => string;

/** Endpunkte des Adressbuchs. Die Darstellung liegt bewusst daneben, nicht hier. */
export function createM1MemberAddressBookAdapter(api: ApiClient): SharedBillingAddressBookAdapter {
  return {
    list: async () => api.get<SharedBillingAddress[]>("/api/v1/billing/addresses"),
    save: async (address) => api.put<SharedBillingAddress>("/api/v1/billing/addresses", address),
    remove: async (addressId) => {
      await api.del(`/api/v1/billing/addresses/${encodeURIComponent(addressId)}`);
    },
    setDefault: async (addressId, kind) => {
      await api.post(`/api/v1/billing/addresses/${encodeURIComponent(addressId)}/default/${kind}`, {});
    },
  };
}

/**
 * Editor als Drawer — m1member-Standard. Die uebrigen Produkte reichen ein Modal herein,
 * my-vitalteam eines, das mobil zuerst funktioniert.
 */
export function M1MemberAddressEditorShell(props: SharedBillingAddressEditorShellProps) {
  return (
    <FormDrawer
      dirty={props.dirty}
      title={props.title}
      open={props.open}
      onCancel={props.onCancel}
      formId={props.formId}
      submitting={props.submitting}
    >
      {props.children}
    </FormDrawer>
  );
}

/**
 * Adressliste als `SettingsListPage` — in m1member laufen Listen ueber diesen Baustein, damit
 * Spalteneditor, Sortierpersistenz und Rahmen ueberall gleich sind.
 */
export function createM1MemberAddressListRenderer(text: Translate) {
  return function renderAddressList(ctx: SharedBillingAddressListContext) {
    return <M1MemberAddressList ctx={ctx} text={text} />;
  };
}

function M1MemberAddressList({ ctx, text }: { ctx: SharedBillingAddressListContext; text: Translate }) {
  // Stabile request-Identitaet: SettingsListPage laedt bei jeder neuen Funktion neu.
  const addresses = ctx.addresses;
  const requestAddresses = React.useCallback(
    async () => ({ data: addresses, success: true, total: addresses.length }),
    [addresses]
  );
  const columnDefinitions: ColumnDefinition[] = [
    { key: "label", label: text("Label"), defaultVisible: true },
    { key: "address", label: text("Address"), defaultVisible: true },
    { key: "defaults", label: text("Status"), defaultVisible: true },
    { key: "vatId", label: text("VAT ID"), defaultVisible: false },
    { key: "billingEmail", label: text("Billing email"), defaultVisible: false },
  ];

  const allColumnsMap: Record<string, ProColumnsWithMeasure<SharedBillingAddress>> = {
    label: {
      title: text("Label"),
      sortValue: (row) => row.label || row.companyName || text("Address"),
      key: "label",
      render: (_, row) => row.label || row.companyName || text("Address"),
    },
    address: {
      title: text("Address"),
      key: "address",
      sortValue: (row) =>
        [row.addressLine1, row.addressLine2, [row.postalCode, row.city].filter(Boolean).join(" "), row.country]
          .filter(Boolean)
          .join(", "),
      render: (_, row) =>
        [row.addressLine1, row.addressLine2, [row.postalCode, row.city].filter(Boolean).join(" "), row.country]
          .filter(Boolean)
          .join(", "),
    },
    vatId: { title: text("VAT ID"), dataIndex: "vatId", key: "vatId" },
    billingEmail: { title: text("Billing email"), dataIndex: "billingEmail", key: "billingEmail" },
    defaults: {
      title: text("Status"),
      key: "defaults",
      sortValue: (row) => [
        row.isDefaultBilling ? text("Default billing") : "",
        ctx.showShipping && row.isDefaultShipping ? text("Default shipping") : ""
      ].filter(Boolean).join(", "),
      render: (_, row) => (
        <Space wrap>
          {row.isDefaultBilling ? <Tag color="blue">{text("Default billing")}</Tag> : null}
          {ctx.showShipping && row.isDefaultShipping ? <Tag color="green">{text("Default shipping")}</Tag> : null}
        </Space>
      ),
    },
  };

  return (
    <SettingsListPage<SharedBillingAddress>
      storageKey="billing-addresses"
      rowKey="id"
      request={requestAddresses}
      columnDefinitions={columnDefinitions}
      allColumnsMap={allColumnsMap}
      onAdd={ctx.onCreate}
      addButtonText={text("Add address")}
      actionsColumn={makeActionColumn<SharedBillingAddress>({ slotCount: 4 })}
      actions={[
        listActionPreset<SharedBillingAddress>("edit", { onClick: (row) => ctx.onEdit(row) }),
        {
          key: "defaultBilling",
          label: text("Use for billing"),
          visible: (row) => !row.isDefaultBilling,
          onClick: (row) => ctx.onSetDefault(row, "billing"),
        },
        {
          key: "defaultShipping",
          label: text("Use for shipping"),
          visible: (row) => ctx.showShipping && !row.isDefaultShipping,
          onClick: (row) => ctx.onSetDefault(row, "shipping"),
        },
        listActionPreset<SharedBillingAddress>("delete", {
          visible: (row) => ctx.canRemove(row),
          confirm: (row) => ({
            title: text("Remove address?"),
            content: <>{row.label || row.companyName}</>,
            okText: text("Remove"),
            cancelText: text("Cancel"),
          }),
          onClick: (row) => ctx.onRemove(row),
        }),
      ]}
    />
  );
}
